<template>
    <div
        class="gallery-item"
        :class="itemClasses"
        :data-id="item.id"
        @mouseenter="doShowActions"
        @mouseleave="showActions = false"
        v-on:click.enter="doSelectMediaItem"
    >
        <slot name="select" :item="item">
            <label
                v-if="selectable"
                class="gallery-item-checkbox-label"
                :class="{'is-checked': itemChecked}"
                :for="`checkbox-${item.id}`"
                @click.stop="doSelectMediaItem"
            >
                <input
                    v-if="selectable"
                    type="checkbox"
                    :id="`checkbox-${item.id}`"
                    class="gallery-item-checkbox"
                    :value="item.id"
                    :checked="itemChecked"
                />
            </label>
        </slot>

        <div class="gallery-link" v-on:click="doMediaItemClick">
            <div class="gallery-thumb-container">
                <thumbnail :item="item" :key="item.timestamp" />
                <media-icon v-if="!condensed" :item="item" />
                <media-comments :item="item" />
            </div>

            <div class="d-flex flex-column w-100">
                <div class="gallery-item-title-wrap">
                    <click-to-edit
                        v-if="editTitle"
                        class="gallery-item-title flex-grow"
                        :value="item.title"
                        @input="doEditTitle(item, $event)"
                        tag="h5"
                        :fullwidth="true"
                        truncate
                    />
                    <h5 v-else class="gallery-item-title single-line flex-grow">
                        {{ item.title }}
                    </h5>
                    <div
                        v-if="item.versionCount > 1"
                        class="badge version-badge"
                        title="File Revision"
                        v-tippy="{arrow: true}"
                    >
                        {{ item.file.badge | capitalize }}
                    </div>
                </div>

                <div class="gallery-item-meta single-line">
                    <div v-show="!showActions" class="gallery-item-meta-detail">
                        <i class="fa fa-clock"></i>
                        <span title="Last updated" v-tippy="{arrow: true}">{{
                            item.lastUpdated
                        }}</span>
                        {{ item.uploadedBy }}
                    </div>

                    <div v-show="showActions" class="gallery-item-actions">
                        <slot name="meta" :item="item"></slot>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {has, capitalize} from 'lodash';
import pluralize from 'pluralize';

import Thumbnail from '@/components/media/Thumbnail';
import MediaIcon from '@/components/media/MediaIcon';
import MediaComments from '@/components/media/MediaComments';
import ClickToEdit from '@/components/click-to-edit';

// mixins
import editTitle from '@/components/media/mixins/editTitle';
import filesize from '@/components/media/mixins/filesize';

export default {
    name: 'MediaItem',
    mixins: [editTitle, filesize],
    components: {
        Thumbnail,
        MediaIcon,
        MediaComments,
        ClickToEdit,
    },
    filters: {
        capitalize,
    },
    props: {
        item: {
            type: Object,
            required: true,
        },
        editTitle: {
            type: Boolean,
            default: false,
        },
        isChecked: {
            type: Boolean,
            default: false,
        },
        setter: {
            type: String,
            required: false,
        },
        selectable: {
            type: Boolean,
            default: true,
        },
        itemRoute: {
            type: [Object, Function, String],
            required: true,
        },
        condensed: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            showActions: false,
            itemChecked: false,
        };
    },
    watch: {
        isChecked: {
            immediate: true,
            handler(newValue) {
                this.itemChecked = newValue;
            },
        },
    },
    computed: {
        showVersionBadge() {
            return this.item.hasFiles && this.item.file.badge !== 'v1';
        },
        itemClasses() {
            return {
                ...this.item.itemClasses,
                'is-super': this.user.super,
                'is-condensed': this.condensed,
            };
        },
    },
    methods: {
        doSelectMediaItem(e) {
            e.preventDefault();
            e.stopPropagation();
            if (this.setter && (e.shiftKey || e.currentTarget.tagName === 'LABEL')) {
                this.$store.commit(this.setter, {
                    id: this.item.id,
                    checked: !this.itemChecked,
                    title: this.item.title,
                    type: this.item.type,
                    public: this.item.public,
                    canReview: this.item.canReview,
                    canShare: this.item.canShare,
                    canDownload: this.item.canDownload,
                    size: this.getItemFilesize(this.item),
                });

                this.$emit('media-item-clicked', {e, item: this.item});
            }
        },
        doMediaItemClick(e) {
            e.preventDefault();
            if (e.shiftKey) {
                this.doSelectMediaItem();
            } else {
                this.$router.push(this.itemRoute);
            }
        },
        doShowActions(e) {
            this.showActions = this.item.isOk || this.user.super;
        },
        pluralize,
    },
};
</script>

<style lang="scss" scoped>
@import '~/sass/_variables';

.gallery-item {
    position: relative;
    cursor: pointer;
    width: 100%;

    &.is-pending,
    &.is-processing {
        &:before {
            content: '';
            display: flex;
            align-items: flex-start;
            justify-content: center;
            position: absolute;
            width: 100%;
            height: 100%;
            left: 0;
            top: 0;
            z-index: 4;
            background: rgba(white, 0.6);
            font-weight: 600;
            padding-top: 21%; // try and align text to center of image
            color: #28a5d4;
            font-size: 2rem;
            font-style: italic;
            cursor: not-allowed;
        }
    }

    &.is-pending:after,
    &.is-processing:after {
        content: 'Upload processing...';
        position: absolute;
        background: #fff;
        padding: 0.6em 1.2em;
        border-radius: 3px;
        z-index: 5;
        top: 25%;
        left: 50%;
        text-align: center;
        font-style: italic;
        transform: translate(-50%, 0);
        box-shadow: 0 0 1em rgba(0, 0, 0, 0.1);
    }

    &.is-invalid {
        box-shadow: 0 0 0 1px map-get($ca-branding, 'error');

        &:before {
            content: none;
        }

        &.is-pending:after,
        &.is-processing:after {
            content: 'Error processing, please try again...';
            box-shadow: 0 0 1em rgba(map-get($ca-branding, 'error'), 0.1);
            color: map-get($ca-branding, 'error');
        }
    }

    .gallery-item-meta {
        min-height: 25px;
    }

    .gallery-item-actions {
        display: flex;
        position: relative;
        flex-direction: row;
        flex-wrap: wrap;
        z-index: 5;
    }

    .gallery-item-title {
        margin-right: 1rem;

        &-wrap {
            display: flex;
            flex-direction: row;
            align-items: center;

            .version-badge {
                max-height: 20px;
            }
        }
    }
}
</style>
